import React, { useEffect, useMemo, useState } from 'react';

import {
	Button,
	Confirm,
	Datepicker,
	Modal,
	SpinnerOverlay,
} from '@components/common';
import ProfileTabSection from '@components/common/ProfileTabSection';
import Table from '@components/common/Table';
import CellForm from '@components/ModalComponents/CellForm';

import { useQueryCrowdCellCard } from '@hooks/crowdCells/useQueryCrowdCellCard';
import { useQueryCrowdCellConfig } from '@hooks/crowdCells/useQueryCrowdCellConfig';
import { useQueryCrowdCellDatesExist } from '@hooks/crowdCells/useQueryCrowdCellDatesExist';
import { useManageCellsBySitesLimenet } from '@hooks/useManageCellBySitesLimenet';

import { startOfDayInUTC } from '@utils/kpi-utils/date';

import { ICell4GDTO, ICell5GDTO } from '@interfaces/cells/dto/cellsDTO';
import { TNameTitle } from '@interfaces/common/common';
import { ICrowdCellCard } from '@interfaces/crowdcell/crowdCells';
import { ITechnologyCell } from '../interfaces';

import { ETechnologiesLabels, ETechnologiesValues, EVendorsValues, TTechnologiesValues } from '@enums/common';

import { MODAL_WIDTH_480, MODAL_WIDTH_580 } from '@constants/modals';
import {
	ENodeTechnologyType,
	limenetNodesTabs,
	limenetTechnologyTabs,
	nodeType,
} from '../constants';

import NodeConfiguration from '../NodeConfiguration';
import { getColumns } from './columns';
import TechnologyCard from './TechnologyCard';

import styles from '../LimenetProfile.module.scss';

interface ILimenetTechnologyTableProps {
	crowdCellId: string;
	isEditAccess: boolean;
}

const LimenetTechnologyTable = ({ crowdCellId, isEditAccess }: ILimenetTechnologyTableProps) => {
	const { crowdCellCard } = useQueryCrowdCellCard(crowdCellId);

	const existingTechnologies = useMemo(() => {
		return crowdCellCard?.technologies?.map(({ configType }: { configType: TNameTitle }) => {
			return configType?.name;
		}) || [];
	}, [crowdCellCard]);

	const [currentTab, setCurrentTab] = useState<TTechnologiesValues | undefined>();
	const [technologyTimestamp, setTimestamp] = useState<Record<string, number | null> | null>(null);
	const [editCell, setEditCell] = useState<any>(null);
	const [clearCell, setClearCell] = useState<number | null>(null);

	const [isShownEditModal, setIsShownEditModal] = useState<boolean>(false);
	const [isShownClearModal, setIsShownClearModal] = useState<boolean>(false);
	const [isShownConfiguration, setIsShownConfiguration] = useState<boolean>(false);
	const [isDirtyFormModal, setIsDirtyFormModal] = useState(false);

	const openEditCellModal = () => setIsShownEditModal(prevState => !prevState);
	const openClearCellModal = () => setIsShownClearModal(prevState => !prevState);
	const openConfigurationModal = () => setIsShownConfiguration(prevState => !prevState);

	const closeEditCellModal = () => setIsShownEditModal(false);
	const closeCleanCellModal = () => setIsShownClearModal(false);
	const closeConfigurationModal = () => setIsShownConfiguration(false);

	const currentTabTimestamp = technologyTimestamp && currentTab ? technologyTimestamp[currentTab] : null;

	const datePickerChangeHandler = (date: number | null) => {
		setTimestamp((prev) => currentTab && prev ? { ...prev, [currentTab]: date } : { ...prev });
	};

	const {
		crowdCellConfigDateExists,
		isInitialLoadingCrowdCellConfigDateExists,
		isLoadingCrowdCellConfigDateExists,
	} = useQueryCrowdCellDatesExist({
		crowdCellId,
		type: currentTab,
	});

	const {
		crowdCellConfig,
		isLoadingCrowdCellConfig,
		isFetchingCrowdCellConfig,
		isInitialLoadingCrowdCellConfig,
	} = useQueryCrowdCellConfig({
		crowdCellId,
		type: currentTab,
		timestamp: startOfDayInUTC(new Date(currentTabTimestamp!)),
	});

	const isLoadingCrowdCellDate = isLoadingCrowdCellConfigDateExists || isInitialLoadingCrowdCellConfig;

	const {
		isLoadingForm,
		isLoadingCellTable,
		handleClearCell,
		updateCell4GFddHandler,
		updateCell4GTddHandler,
		updateCell5GHandler,
	} = useManageCellsBySitesLimenet({
		technology: currentTab,
		closeClearConfirm: closeCleanCellModal,
		closeCellModal: closeEditCellModal,
	});

	useEffect(() => {
		setCurrentTab(existingTechnologies?.at(0) as TTechnologiesValues || undefined);
	}, [existingTechnologies]);

	useEffect(() => {
		if (crowdCellConfigDateExists && !currentTabTimestamp) {
			setTimestamp((prev) => {
				return currentTab
					? { ...prev, [currentTab]: crowdCellConfigDateExists.at(-1) || null }
					: { ...prev };
			});
		}
	}, [crowdCellConfigDateExists, crowdCellId, currentTab, currentTabTimestamp]);

	const nodes = useMemo(() =>
			limenetNodesTabs.filter((node) => !!crowdCellCard?.[node.title.toLocaleLowerCase() as keyof ICrowdCellCard])
		, [crowdCellCard]);

	const technologyTabs = crowdCellCard?.technologies?.length
		? limenetTechnologyTabs.filter((tab) => existingTechnologies.includes(tab.value))
		: [];

	const tabs = [...technologyTabs, ...nodes];

	const findCard = (list?: ITechnologyCell[]) => {
		if (!list) return null;

		const card = list.find((item) => item.configType.name === currentTab);

		if (!card) return null;

		return card?.name === ETechnologiesLabels.C_5G
			? TechnologyCard({ card: card?.[ENodeTechnologyType.globalGnbId], nodeType: nodeType.gNode })
			: TechnologyCard({ card: card?.[ENodeTechnologyType.globalEnbId], nodeType: nodeType.eNode });
	};

	const columns = useMemo(() => {
		return currentTab
			? getColumns({
				technology: currentTab,
				editCell: setEditCell,
				clearCell: setClearCell,
				openClearCellModal,
				openEditCellModal,
				isEditAccess,
			})
			: [];
	}, [currentTab, isEditAccess]);

	const getTab = () => {
		const isLoadingTable = (isLoadingCrowdCellConfig && !isShownConfiguration) || isLoadingCellTable;

		return (
			<div className={styles.technologyTab}>
				{(isLoadingCrowdCellDate || isInitialLoadingCrowdCellConfigDateExists) && <SpinnerOverlay />}
				{findCard(crowdCellCard?.technologies)}
				<div className={styles.tableControlPanel}>
					{crowdCellConfigDateExists && (
						<Datepicker
							disableFutureDates
							disableInput
							disabled={!crowdCellConfigDateExists.length}
							handleChange={datePickerChangeHandler}
							includeDates={crowdCellConfigDateExists as any}
							isMobileDevice={false}
							isTodayButton={false}
							selected={currentTabTimestamp ? new Date(currentTabTimestamp) : null}
						/>
					)}
					<Button
						large
						onClick={openConfigurationModal}
						text='View configuration'
					/>
				</div>
				{crowdCellConfig?.cells && (
					<Table
						columns={columns}
						data={crowdCellConfig.cells}
						isLoading={isLoadingTable && isFetchingCrowdCellConfig}
						isWithoutCheckbox
						isWithoutDeleteAction
						isWithoutFilter
						pagination={{
							countOfRecords: crowdCellConfig.cells.length,
							withoutPagination: true,
						}}
					/>
				)}
			</div>
		);
	};

	const cleanHandler = () => {
		if (!clearCell || !currentTabTimestamp) return null;

		handleClearCell({ cellId: clearCell as number, id: crowdCellId, timestamp: currentTabTimestamp });
	};

	const submitHandler = ({ values, cellId }: { values: ICell5GDTO | ICell4GDTO; cellId: number }) => {
		if (!currentTabTimestamp) return null;

		const updateSubmitData = {
			id: crowdCellId,
			cellId: cellId,
			dto: { ...values },
			timestamp: currentTabTimestamp,
		};

		switch (currentTab) {
			case ETechnologiesValues.C_4G_FDD:
				updateCell4GFddHandler(updateSubmitData);
				break;
			case ETechnologiesValues.C_4G_TDD:
				updateCell4GTddHandler(updateSubmitData);
				break;
			case ETechnologiesValues.C_5G:
				updateCell5GHandler(updateSubmitData);
				break;
			default:
				return;
		}
	};

	return (
		<>
			<Modal
				isConfirmDialogNeededOnClose
				isDirtyForm={isDirtyFormModal}
				isOpen={isShownEditModal}
				onClose={closeEditCellModal}
				title='Edit cell'
				width={MODAL_WIDTH_580}
			>
				<CellForm
					editableCell={editCell}
					is4GFdd={currentTab === ETechnologiesValues.C_4G_FDD}
					is4GTdd={currentTab === ETechnologiesValues.C_4G_TDD}
					is5G={currentTab === ETechnologiesValues.C_5G}
					isLoadingForm={isLoadingForm}
					setIsDirtyFormModal={setIsDirtyFormModal}
					submitHandler={submitHandler}
					toggleCellModal={openEditCellModal}
					vendor={EVendorsValues.limenet}
				/>
			</Modal>

			<Modal
				isOpen={isShownClearModal}
				onClose={closeCleanCellModal}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					onSubmit={cleanHandler}
					question='Are you sure you want to clear the Cell?'
				/>
			</Modal>

			<Modal
				height='80%'
				isOpen={isShownConfiguration}
				onClose={closeConfigurationModal}
				title='View configuration'
				width={MODAL_WIDTH_580}
			>
				<NodeConfiguration
					closeModal={closeConfigurationModal}
					crowdCellId={crowdCellId}
					date={currentTabTimestamp}
					technology={currentTab}
				/>
			</Modal>
			<ProfileTabSection
				currentTab={currentTab || ''}
				getCurrentTab={getTab}
				isWithoutShadow
				maxWidth='none'
				setCurrentTab={setCurrentTab}
				tabWidth='170px'
				tabs={tabs}
			/>
		</>
	);
};

export default LimenetTechnologyTable;
